import CommercialRegister, { SegmentedControl } from "@/components/CommercialRegister";
// Customer Invoices (outgoing). Office only. PDF + email + mark paid.
import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { PageHeader } from "@/pages/dashboard/_shared";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS = {
  draft: { label: "Draft", cls: "bg-zinc-100 text-zinc-700 border-zinc-300" },
  sent: { label: "Due", cls: "bg-sky-100 text-sky-800 border-sky-400" },
  paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-800 border-emerald-400" },
  overdue: { label: "Overdue", cls: "bg-red-100 text-red-800 border-red-400" },
  cancelled: { label: "Cancelled", cls: "bg-zinc-200 text-zinc-600 border-zinc-400" },
};

const TABS = [
  { key: "draft", label: "Drafts" },
  { key: "sent", label: "Due" },
  { key: "overdue", label: "Overdue" },
  { key: "paid", label: "Paid" },
  { key: "all", label: "All" },
];

export default function CustomerInvoices() {
  const [items, setItems] = useState([]);
  const requestRef = useRef(null);
  const [tab, setTab] = useState("draft");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [xeroConnected, setXeroConnected] = useState(false);

  useEffect(() => {
    let ok = true;
    api.get("/xero/status").then(({ data }) => { if (ok) setXeroConnected(!!data?.connected); }).catch(() => {});
    return () => { ok = false; };
  }, []);

  const load = async () => {
    requestRef.current?.abort();
    const controller = new AbortController(); requestRef.current = controller;
    setLoading(true); setLoadError(false);
    try {
      const { data } = await api.get("/customer-invoices", { params: { status: tab }, signal: controller.signal });
      if (!controller.signal.aborted) setItems(data || []);
    } catch {
      if (!controller.signal.aborted) { setLoadError(true); setItems([]); }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => requestRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const navigate = useNavigate();
  const openNew = () => navigate("/app/customer-invoices/new");

  // Deep link from the Job billing report: /app/customer-invoices?new_for_job={id}
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const jid = searchParams.get("new_for_job");
    if (jid) navigate(`/app/jobs/${jid}/charge-up`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const openEdit = (inv) => navigate(`/app/customer-invoices/${inv.id}`);

  const onSaved = (saved) => {
    setItems((prev) => {
      const i = prev.findIndex((x) => x.id === saved.id);
      if (i >= 0) {
        const next = prev.slice();
        next[i] = saved;
        return next;
      }
      return [saved, ...prev];
    });
  };

  const del = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}? Can't be undone.`)) return;
    try {
      await api.delete(`/customer-invoices/${inv.id}`);
      setItems((p) => p.filter((x) => x.id !== inv.id));
      toast.success("Invoice deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const chase = async (inv) => {
    let to = inv.client_email;
    if (!to) {
      to = window.prompt(`No email saved for ${inv.client_name || "this customer"}.\n\nSend the payment reminder to:`);
      if (!to) return;
    } else if (!window.confirm(`Email a payment reminder for ${inv.invoice_number} to ${to}?`)) {
      return;
    }
    try {
      const { data } = await api.post(`/customer-invoices/${inv.id}/chase`, { to_email: to });
      toast.success(`Payment reminder sent to ${data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the reminder");
    }
  };

  const markPaid = async (inv) => {
    const amt = window.prompt(`Mark INV ${inv.invoice_number} as paid?\n\nAmount received (default $${(inv.total || 0).toFixed(2)})`, (inv.total || 0).toFixed(2));
    if (amt === null) return;
    const paid_amount = parseFloat(amt) || inv.total;
    try {
      const { data } = await api.patch(`/customer-invoices/${inv.id}`, {
        status: "paid",
        paid_amount,
        paid_method: "bank transfer",
      });
      onSaved(data);
      toast.success(`Marked paid · $${paid_amount.toFixed(2)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const markSent = async (inv) => {
    if (!window.confirm(`Approve INV ${inv.invoice_number}?\n\nThis finalises the invoice and moves it from Drafts to Due. No email is sent — use "Send Invoice" if you want to email the customer.`)) return;
    try {
      const { data } = await api.patch(`/customer-invoices/${inv.id}`, { status: "sent" });
      onSaved(data);
      toast.success(`INV ${inv.invoice_number} approved — moved to Due`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const pushToXero = async (inv) => {
    if (!window.confirm(`Push INV ${inv.invoice_number} to Xero as a DRAFT?\n\nIt'll appear in your Xero drafts ready for approval — won't be emailed automatically.`)) return;
    const tid = toast.loading("Pushing to Xero…");
    try {
      const { data } = await api.post(`/xero/invoices/${inv.id}/push`);
      toast.dismiss(tid);
      toast.success("Pushed to Xero", { description: "Tap the Xero link in the row to open it." });
      onSaved({ ...inv, xero_invoice_id: data.xero_invoice_id, xero_invoice_url: data.xero_invoice_url });
    } catch (e) {
      toast.dismiss(tid);
      const detail = e.response?.data?.detail || "Push failed";
      toast.error(detail.includes("not connected") ? "Connect Xero first under Account → Xero" : detail);
    }
  };

  return (
    <div data-testid="customer-invoices-page" className="px-4 py-4 md:p-6 lg:p-6 max-w-[1440px] mx-auto pb-24 lg:pb-10">
      <PageHeader
        tag="/ Sales"
        title="Customer invoices"
        subtitle="Bill your customers. Approved variations auto-attach when you link a job."
        action={
          <Button onClick={openNew} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs" data-testid="invoice-new">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New invoice
          </Button>
        }
      />

      <div className="mb-4"><SegmentedControl label="Invoice status" options={TABS} value={tab} onChange={setTab} testId="customer-invoices-tabs" /></div>
      <CommercialRegister kind="invoice" items={items} loading={loading} error={loadError} onRetry={load} statuses={STATUS} onOpen={openEdit}
        actions={inv => [
          {label:'Open PDF', href:`${api.defaults.baseURL}/customer-invoices/${inv.id}/pdf`, testId:`invoice-pdf-${inv.id}`},
          inv.status === 'draft' && {label:'Approve invoice', onClick:() => markSent(inv), testId:`invoice-mark-sent-${inv.id}`},
          inv.status !== 'paid' && inv.status !== 'draft' && {label:'Send payment reminder', onClick:() => chase(inv), testId:`invoice-chase-${inv.id}`},
          inv.status !== 'paid' && inv.status !== 'draft' && {label:'Record payment', onClick:() => markPaid(inv), testId:`invoice-mark-paid-${inv.id}`},
          inv.xero_invoice_id ? {label:'Open in Xero', href:inv.xero_invoice_url || `https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${inv.xero_invoice_id}`, testId:`invoice-xero-view-${inv.id}`} : xeroConnected && {label:'Send draft to Xero', onClick:() => pushToXero(inv), testId:`invoice-push-xero-${inv.id}`},
          {label:'Delete invoice', onClick:() => del(inv), destructive:true, testId:`invoice-del-${inv.id}`},
        ]}
      />

    </div>
  );
}
